/**
 * The signed-in session, read once from `/api/auth/me`.
 *
 * `canSee(key)` answers from the navigation list the SERVER sent (menu
 * entries); `can(...keys)` from its permission keys (controls inside a screen:
 * buttons, tabs, row actions). There is no role check anywhere in the
 * frontend: the client renders a decision, it does not make one, and every
 * endpoint re-checks on its own. A control the caller may not use is not
 * rendered at all, not disabled.
 */

import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/api/client';
import { queryKeys, useSessionQuery } from '@/api/queries';
import type { Session, UserAccount } from '@/api/types';
import { holdsAny, type PermissionKey } from '@/session/permissions';

const NONE: ReadonlySet<string> = new Set();

interface SessionValue {
  user: UserAccount | null;
  navigation: readonly string[];
  isLoading: boolean;
  /** A failure other than "not signed in" (API down, 500). */
  error: Error | null;
  canSee: (key: string) => boolean;
  /** Whether the caller holds any of the keys. */
  can: (...keys: PermissionKey[]) => boolean;
  permissions: ReadonlySet<string>;
  /** Whether MCP access is on in this environment (off until a session says otherwise). */
  mcpEnabled: boolean;
  mcpUrl: string | null;
  /** Whether MCP's write tools are offered (on unless the environment switched them off). */
  writesEnabled: boolean;
}

const SessionContext = createContext<SessionValue | null>(null);

const isUnauthenticated = (error: Error | null) => error instanceof ApiError && error.status === 401;

function mcpValues(session: Session | null) {
  return {
    mcpEnabled: session?.mcp_enabled ?? false,
    mcpUrl: session?.mcp_url ?? null,
    writesEnabled: session?.integration_writes_enabled ?? true,
  };
}

function sessionValue(session: Session | null, error: Error | null, isPending: boolean): SessionValue {
  const unauthenticated = isUnauthenticated(error);
  const navigation = session?.navigation ?? [];
  const permissions = session ? new Set(session.permissions) : NONE;
  return {
    user: session?.user ?? null,
    navigation,
    isLoading: isPending && !unauthenticated,
    error: unauthenticated ? null : error,
    canSee: (key: string) => navigation.includes(key),
    can: (...keys: PermissionKey[]) => holdsAny(permissions, ...keys),
    permissions,
    ...mcpValues(session),
  };
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const query = useSessionQuery();
  const client = useQueryClient();

  // Any 401 anywhere means the session ended (expired, password changed,
  // account deactivated): drop it, and the router sends the user to sign in.
  useEffect(() => {
    const onUnauthorized = () => client.setQueryData(queryKeys.session, null);
    window.addEventListener('wynxx:unauthorized', onUnauthorized);
    return () => window.removeEventListener('wynxx:unauthorized', onUnauthorized);
  }, [client]);

  const value = useMemo(
    () => sessionValue(query.data ?? null, query.error, query.isPending),
    [query.data, query.error, query.isPending],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside <SessionProvider>.');
  return value;
}

/** Renders its children only when the caller holds one of the permissions. */
export function Can({ permission, children }: { permission: PermissionKey | PermissionKey[]; children: ReactNode }) {
  const { can } = useSession();
  const keys = Array.isArray(permission) ? permission : [permission];
  return can(...keys) ? <>{children}</> : null;
}
